"use client";

import { useState, useEffect } from "react";

// Define the static structure of nav links (excluding the 'محصولات' dropdown contents)
const initialStaticNavLinks = [
  // This will be filled dynamically
  {
    label: "فروش",
    dropdown: [
      { label: "بخشنامه فروش", href: "/saleCircular" },
      { label: "اطلاعیه های فروش", href: "/salesAnnouncements" },
      { label: "اعلام نتایج قرعه کشی", href: "/lotteryResults" },
      { label: "قانون حمایت از مصرف کننده", href: "/consumer" },
      { label: "فایل فرمت خام قرارداد فروش", href: "/contract.pdf", newTab: true },
      { label: "فایل راهنمای خرید اینترنتی", href: "/sale-guidance.pdf", newTab: true },
      {
        label: "فروش آنلاین",
        href: "https://sale.barsavosh.co/Subscriber/Login",
        newTab: true,
      },
    ],
  },
  {
    label: "خدمات پس از فروش",
    dropdown: [
      {
        label: "گارانتی و فنی و مهندسی",
        dropdown: [
          { label: "واحد گارانتی", href: "/warranty" },
          { label: "شرایط ابطال و اعمال گارانتی", href: "/warrantyEXP" },
          { label: "نمونه فاکتور", href: "/invoice-sample.pdf", newTab: true },
          { label: "فنی و مهندسی", href: "/engineering" },
          { label: "مشاهده سرویس های ادواری", href: "/maintenance" },
        ]
      },
      {
        label: "تعمیرگاه و قطعات",
        dropdown: [
          { label: "سرویس در محل", href: "/homeServices", dropdown: [] },
          { label: "استعلام اصالت نرخ خدمات", href: "https://aftersale.barsavosh.co/serviceprice", newTab: true },
          { label: "استعلام اصالت نرخ قطعات", href: "https://aftersale.barsavosh.co/partprice", newTab: true },
          { label: "سوابق تعمیرات", href: "https://aftersale.barsavosh.co/", newTab: true },
          { label: "لیست قطعات عیوب ایمنی", href: "/safetyList" },
          { label: "گارانتی قطعات با عمر محدود", href: "/warranty-limitted-parts.pdf", newTab: true },
          { label: "تعمیرگاه مرکزی", href: "/repairshop" },
          { label: "واحد امداد", href: "/emdad" },
          { label: "فراخوان و اطلاعیه ها", href: "/aftersales/recalls" },
        ]
      }
    ],
  },
  {
    label: "امور مشتریان",
    dropdown: [
      { label: "صدای مشتری", href: "https://crm.barsavosh.co/Crm/Create", imageSrc: "/customerVoice.png", newTab: true },
      { label: "نظر سنجی مشتریان", href: "/surveys", imageSrc: "/CustomerSurvey2.png" },
      { label: "درباره ما", href: "/aboutUs", imageSrc: "/CC.png" },
      { label: "قانون حمایت از مصرف کنندگان", href: "/consumer" },
      { label: "پرسش های متداول(FAQ)", href: "/faq" },
    ],
  },
  {
    label: "نمایندگی ها",
    // We remove the top-level href so it triggers the dropdown logic instead of direct navigation
    dropdown: [
      { label: "مشاهده نمایندگی ها", href: "/dealers" },
      { label: "اخذ نمایندگی", href: "/dealerRegistration" }
    ]
  },
  {
    label: "نوبت دهی اینترنتی",
    dropdown: [
      { label: "سرویس در محل", href: "/homeServices", newTab: true },
      { label: "نوبت دهی تعمیرگاه", href: "/repairshopReserve", newTab: true }
    ]
  },
  { label: "امضای قرارداد", href: "https://contract.barsavosh.co/", dropdown: [], isSpecial: true },
];

export function useNavData() {
  const [navLinks, setNavLinks] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

  useEffect(() => {
    const fetchAndIntegrateProducts = async () => {
      // 1. Safety Check: Is the Env Var loaded?
      if (!API_BASE_URL) {
        console.error("Critical: NEXT_PUBLIC_API_BASE_URL is not defined.");
        setLoadingProducts(false);
        return;
      }

      try {
        // 2. Safe URL Construction: Handles whether API_BASE_URL has a slash at the end or not
        const baseUrl = API_BASE_URL.endsWith("/")
          ? API_BASE_URL
          : `${API_BASE_URL}/`;

        const finalUrl = `${baseUrl}car/list`;

        console.log("Attempting to fetch:", finalUrl); // Debugging log

        const res = await fetch(finalUrl);

        if (!res.ok) {
          // This handles 404 or 500 errors
          throw new Error(`Server responded with ${res.status}: ${res.statusText}`);
        }

        const carData = await res.json();

        // Define preferred IDs for specific models to ensure the correct image/video is used
        const preferredModelIds = {
          "RAV4": 22,
          "Camry": 20,
          "Corolla Cross": 18,
          "corolla": 27,
        };

        const groupedCarsByCategory = {};

        carData.forEach(car => {
          // The body style or category is stored in car.trim
          const category = car.trim || "متفرقه";

          if (!groupedCarsByCategory[category]) {
            groupedCarsByCategory[category] = {};
          }

          // Use the logic from ProductModelSwitcher to find the main model name
          const modelName = car.car_model?.name || car.english_name.split(' ')[0];

          // Case-insensitive lookup (so "corolla" matches "Corolla")
          const matchedKey = Object.keys(preferredModelIds).find(
            key => key.toLowerCase() === modelName.toLowerCase()
          );
          const preferredId = matchedKey ? preferredModelIds[matchedKey] : undefined;

          // Add if it's the first one we've seen, OR if it matches the exact preferred ID
          if (!groupedCarsByCategory[category][modelName] || car.id === preferredId) {
            groupedCarsByCategory[category][modelName] = {
              label: modelName,
              href: `/products/${car.id}`,
              imageSrc: car.slider_image || car.main_image || car.detail_image_1,
              videoSrc: car.detail_image_1?.includes('.mp4') ? car.detail_image_1 : "/Land-Cruiser.mp4",
            };
          }
        });

        // Convert the nested object into the dropdown array structure
        const productDropdownLinks = Object.keys(groupedCarsByCategory).map(category => ({
          label: category,
          dropdown: Object.values(groupedCarsByCategory[category])
        }));

        productDropdownLinks.push({
          label: "همه ی خودروها",
          href: "/products",
        });

        productDropdownLinks.push({
          label: "ویدئو خودروها",
          href: "/carsMedia",
        });

        const productsLinkObject = {
          label: "خودروها",
          isCarsCategory: true,
          dropdown: productDropdownLinks,
        };
        setNavLinks([productsLinkObject, ...initialStaticNavLinks]);
      } catch (error) {
        // This catches the "Failed to fetch" (Network Error)
        console.error("Error fetching and integrating products:", error);

        // Fallback UI
        setNavLinks([
          {
            label: "خودروها",
            isCarsCategory: true,
            dropdown: [
              { label: "همه ی خودروها", href: "/products" },
              { label: "ویدئو خودرو", href: "/carsMedia" },
            ],
          },
          ...initialStaticNavLinks,
        ]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchAndIntegrateProducts();
  }, [API_BASE_URL]);

  return { navLinks, loadingProducts };
}